import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttendanceManagementModal from '../../components/Modals/AttendanceManagementModal';
import AttendanceSlideModal from '../../components/Modals/AttendanceSlideModal';
import { useAttendance, AttendanceResponseDto } from '../../hooks/useAttendance';
import { useStudents, Student } from '../../hooks/useStudents';
import { useSessionClass } from 'contexts/SessionClassContext';
import EnhancedTable from '../common/EnhancedTable';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'contexts/AuthContext';
import { useTenantHeader } from 'hooks/useTenantHeader';
import ActionButtons from '../common/ActionButtons';
import { useTeacher } from 'hooks/useTeacher';
import { format, parseISO } from 'date-fns';

const AttendanceManagement: React.FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [slideModalOpen, setSlideModalOpen] = useState(false);
  const [editDate, setEditDate] = useState<string | null>(null);
  const { selectedSession, selectedClass } = useSessionClass();

  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);

  const { getAttendanceQuery } = useAttendance({
    sessionId: selectedSession?.id || 0,
    classId: selectedClass?.id || 0,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  const { user } = useAuth();
  const { tenantLoading } = useTenantHeader();
  const { studentsQuery } = useStudents(tenantLoading);
  const { teachersQuery } = useTeacher(user?.schoolId || 0, tenantLoading);

  const handleTakeAttendance = () => {
    setEditDate(null);
    setModalOpen(true);
  };

  const handleTakeAttendanceSlide = () => {
    setSlideModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditDate(null);
    setModalOpen(false);
  };

  const handleCloseSlideModal = () => {
    setSlideModalOpen(false);
  };

  const handleEditAttendance = (date: string) => {
    setEditDate(date);
    setModalOpen(true);
  };

  // Delete functionality is removed as it's not supported by the new API
  const handleCloseDeleteDialog = () => { }; // Placeholder
  const handleDelete = async () => { }; // Placeholder

  const getStudentName = (studentId: number) => {
    const student = studentsQuery.data?.find((s: Student) => s.id === studentId);
    return student ? `${student.studentName}` : '';
  };

  const columns = [
    { id: 'displayDate', label: t('date'), minWidth: 130 },
    { id: 'className', label: t('class'), minWidth: 130 },
    { id: 'teacherName', label: t('teacher'), minWidth: 150 },
    { id: 'statusSummary', label: t('status'), minWidth: 200 },
    { id: 'lastUpdated', label: t('last_updated'), minWidth: 150 },
  ];

  const renderActions = (row: any) => (
    <ActionButtons
      onEdit={() => handleEditAttendance(row.date)}
    />
  );

  const filteredAttendance = getAttendanceQuery.data || [];

  const groupedData = useMemo(() => {
    const groups: Record<string, {
      date: string,
      presents: number,
      absents: number,
      leaves: number,
      total: number,
      classId: number,
      staffId: number | null,
      lastUpdatedTime: string | null
    }> = {};

    filteredAttendance.forEach((record) => {
      const date = record.attendanceDate;
      if (!groups[date]) {
        groups[date] = {
          date,
          presents: 0,
          absents: 0,
          leaves: 0,
          total: 0,
          classId: record.classId,
          staffId: record.staffId || record.teacherId || null,
          lastUpdatedTime: record.lastUpdatedTime || null
        };
      }
      groups[date].total++;
      if (record.statusCode === 'PRESENT') {
        groups[date].presents++;
      } else if (record.statusCode === 'ABSENT') {
        groups[date].absents++;
      } else if (record.statusCode === 'LEAVE') {
        groups[date].leaves++;
      }

      if (record.lastUpdatedTime) {
        const currentGroup = groups[date];
        if (!currentGroup.lastUpdatedTime || record.lastUpdatedTime > currentGroup.lastUpdatedTime) {
          currentGroup.lastUpdatedTime = record.lastUpdatedTime;
        }
      }
    });

    return Object.values(groups).map(group => {
      const teacher = teachersQuery.data?.find(t => t.id === group.staffId);
      const teacherName = teacher ? `${teacher.firstName} ${teacher.lastName}` : t('n/a');

      return {
        ...group,
        displayDate: format(parseISO(group.date), "dd-MM-yyyy"),
        className: selectedClass?.name || String(group.classId),
        teacherName,
        statusSummary: `${t('total')}: ${group.total}, ${t('present')}: ${group.presents}, ${t('absent')}: ${group.absents}, ${t('leave')}: ${group.leaves}`,
        lastUpdated: group.lastUpdatedTime ? format(parseISO(group.lastUpdatedTime), "dd-MM-yyyy HH:mm") : t('n/a'),
      };
    }).sort((a, b) => b.date.localeCompare(a.date));
  }, [filteredAttendance, selectedClass, t, teachersQuery.data]);



  return (
    <Paper sx={{ p: 2, maxWidth: 1500, mx: 'auto', mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">{t('attendance_management')}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" }
            }}
            onClick={handleTakeAttendance}
          >
            {t('take_attendance')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" }
            }}
            onClick={handleTakeAttendanceSlide}
          >
            {t('one_by_one_attendance')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          label={t('start_date')}
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label={t('end_date')}
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Box>

      <EnhancedTable
        columns={columns}
        data={groupedData}
        title={t('attendance_list')}
        searchFields={['className', 'date']}
        filterFields={[]}
        actions={renderActions}
      />

      <AttendanceManagementModal
        open={modalOpen}
        onClose={handleCloseModal}
        initialDate={editDate || undefined}
      />

      <AttendanceSlideModal
        open={slideModalOpen}
        onClose={handleCloseSlideModal}
      />

    </Paper>
  );
};

export default AttendanceManagement;